import { useTranslation } from 'react-i18next';

import { TickMeter } from './ResultRow';
import { SectionHeadCard } from '../../../../components';
import {
  FORMANT_CENTER_GOOD,
  FORMANT_CENTER_SCALE,
  RESONANCE_LEVEL_GOOD,
  RESONANCE_LEVEL_SCALE,
  goodBand,
  meterPosition,
} from '../../resultCopy';

import type { DiagnosisRecord } from '../../record';

/**
 * 싱어즈 포먼트 — 울림이 **어느 대역에** 얼마나 **세게** 모였는지(시안 §8.8 `1000:9158`
 * `핵심 포인트`): 왼쪽에 값(20px Bold + 단위) · 라벨, 오른쪽에 눈금 미터 · 적정 범위 캡션.
 *
 * 시안의 `82 / 100` 도넛과 `F3~F5` 줄은 여기 없다. 점수는 몇 Hz가 몇 점인지 정하는
 * 눈금이 아직 없어 임의 숫자가 되고, F3~F5는 포먼트 추정이라 서버 `/analyze`가 낼 값이다
 * (`RECORD_ENDPOINT`가 비어 있어 지금은 아무도 안 낸다). 못 내는 값을 그리면 화면이
 * 엔진이 못 지키는 약속을 한다 — 낼 수 있는 두 줄만 세운다.
 */

/** 적정 범위 캡션은 kHz로 말한다 — 시안 문구가 `2~4kHz`다. */
const HZ_PER_KHZ = 1000;

export const SingersFormantSection = ({
  record,
}: {
  record: DiagnosisRecord;
}) => {
  const { t } = useTranslation();
  const center = record.resonanceCenterHz;
  const levelDb = record.resonanceLevelDb;

  /**
   * 두 줄은 **같은 모양**이다 — 값의 표기와 자(눈금·적정 범위)만 다르다. 표로 두면 캡션이
   * 말하는 범위와 색칠된 구간이 언제나 같은 상수 짝에서 나온다.
   */
  const rows = [
    {
      key: 'center',
      value: center,
      unit: t('web.result.formant.unitHz'),
      format: (v: number) => Math.round(v).toLocaleString(),
      caption: t('web.result.formant.centerRange', {
        min: FORMANT_CENTER_GOOD.min / HZ_PER_KHZ,
        max: FORMANT_CENTER_GOOD.max / HZ_PER_KHZ,
      }),
      scale: FORMANT_CENTER_SCALE,
      good: FORMANT_CENTER_GOOD,
      intensity: false,
      ticks: [FORMANT_CENTER_SCALE.min, FORMANT_CENTER_SCALE.max].map((hz) => ({
        at: meterPosition(hz, FORMANT_CENTER_SCALE),
        label: t('web.result.formant.tickHz', { value: hz / HZ_PER_KHZ }),
      })),
    },
    {
      key: 'level',
      value: levelDb,
      unit: t('web.result.formant.unitDb'),
      format: (v: number) => v.toFixed(1),
      caption: t('web.result.formant.levelRange', RESONANCE_LEVEL_GOOD),
      scale: RESONANCE_LEVEL_SCALE,
      good: RESONANCE_LEVEL_GOOD,
      // 세기는 색 농도로 말한다(TestFlight #58). 중심 주파수 줄은 위치라 농도가 아니다.
      intensity: true,
      ticks: [
        RESONANCE_LEVEL_SCALE.min,
        RESONANCE_LEVEL_GOOD.min,
        RESONANCE_LEVEL_SCALE.max,
      ].map((db) => ({
        at: meterPosition(db, RESONANCE_LEVEL_SCALE),
        label: String(db),
      })),
    },
  ] as const;

  return (
    <div className="flex flex-col gap-8 py-4">
      <SectionHeadCard
        size="md"
        className="mx-[18px]"
        title={t('web.result.tabs.keyPoints')}
      />
      <ul className="flex flex-col gap-7 px-[22px]">
        {rows.map(
          ({
            key,
            value,
            unit,
            format,
            caption,
            scale,
            good,
            ticks,
            intensity,
          }) => (
            <li key={key} className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 flex-col gap-1.5">
                <p className="flex items-end gap-1 leading-none">
                  {value == null ? (
                    <span className="text-sm font-semibold text-bk-600">
                      {t('web.result.pending')}
                    </span>
                  ) : (
                    <>
                      <span className="text-xl font-bold tabular-nums text-bk-900">
                        {format(value)}
                      </span>
                      <span className="text-sm font-medium text-bk-600">
                        {unit}
                      </span>
                    </>
                  )}
                </p>
                <span className="text-[13px] text-bk-600">
                  {t(`web.result.formant.${key}`)}
                </span>
              </div>
              {value != null && (
                <TickMeter
                  position={meterPosition(value, scale)}
                  good={goodBand(good, scale)}
                  ticks={ticks}
                  caption={caption}
                  intensity={intensity}
                />
              )}
            </li>
          ),
        )}
      </ul>
    </div>
  );
};
